/**
 * First-login onboarding — the full-screen bento flow.
 *
 * A bento grid of cells with Mercado Libre featured 2×2, an action bar
 * underneath, a form sheet that takes the screen when a connector asks for
 * data, and a progress band above the "what next" cells on screen 2.
 *
 * Chosen from three aesthetic variants; the PoCs were deleted once the design
 * was settled and live in git history at commit 3396574f5
 * (`docs/prs/TV1-ONBOARDING/poc/`).
 *
 * ─── It is a VIEW, not the record ───────────────────────────────────────────
 * Connecting a channel and starting its import are performed by
 * `connectionWatchPolicy`, which also writes both facts into the assistant
 * conversation as messages with blocks. That is what makes this closable at any
 * moment without losing anything: the floating assistant renders the same
 * `import-progress` block from the same message. Nothing here is a second copy
 * of that state.
 *
 * ─── Credentials never reach the conversation ───────────────────────────────
 * An api-key connector (today: WooCommerce) is authorized from the inline panel
 * below, holding its values in LOCAL component state. The conversation is
 * persisted server-side as an aggregate, so a secret dispatched into it would be
 * written into a transcript. It is collected here and posted straight to the
 * authorize endpoint — the conversation only ever learns that a channel
 * appeared.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { v7 as uuidv7 } from 'uuid';
import { Image, Loader, PasswordInput, TextInput } from '@mantine/core';
import logo from '@assets/appImages/favicon.png';
import {
  IconArrowRight,
  IconBuildingStore,
  IconCheck,
  IconFileInvoice,
  IconPlugConnected,
  IconShoppingCartDown,
  IconTruck,
  IconUsers,
  IconX
} from '@tabler/icons-react';
import {
  useSharedIntegrationsSelector,
  selectors as integrationSelectors
} from '@domains/integrations/shared/state/store';
import {
  canImportOrderHistory,
  resolveSetupPanelKind
} from '@domains/integrations/shared/state/capabilityGates';
import type { ConnectorEntry } from '@domains/integrations/shared/state/state';
import {
  apiKeyFieldSchemas
} from '@domains/integrations/channels/components/AddChannelModal/AddChannelModal';
import { useAIAssistantActions } from '@domains/aiAssistant';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import { useCan } from '@hooks/useCapability';
import { ImportProgressBlock } from '@components/application/AIAssistantChat/blocks';
import {
  useOnboardingWizardDispatch,
  useOnboardingWizardSelector,
  onboardingWizardSelectors as sel
} from '../../state/store';
import { onboardingWizardTestIds as ids } from '../../testids';
import classes from './OnboardingWizard.module.css';

/**
 * The "what next" cells. Each one hands a written brief to Tessy — the wizard
 * closes, the floating panel opens, and the message is sent as if the user had
 * typed it. `capability` gates the cell: offering "configurá tus usuarios" to
 * someone who cannot manage users is an invitation to a permission error.
 */
const NEXT_OPTIONS = [
  {
    key: 'channel',
    icon: IconPlugConnected,
    capability: 'sales-channels:integrations:provision',
    title: 'Otro canal de venta',
    detail: 'Un solo stock para todos tus canales',
    prompt: 'Quiero conectar otro canal de venta.'
  },
  {
    key: 'invoicing',
    icon: IconFileInvoice,
    capability: 'invoicing:billing-entities:create',
    title: 'Facturación electrónica',
    detail: 'ARCA, punto de venta y comprobantes',
    prompt: 'Quiero configurar la facturación electrónica de mi cuenta.'
  },
  {
    key: 'logistics',
    icon: IconTruck,
    capability: 'stock-management:warehouses:create',
    title: 'Depósitos y logística',
    detail: 'De dónde sale el stock de cada canal',
    prompt: 'Quiero configurar mis depósitos y mi logística.'
  },
  {
    key: 'erp',
    icon: IconBuildingStore,
    capability: 'sales-channels:integrations:provision',
    title: 'Tu ERP',
    detail: 'Sincronizá items, precios y stock',
    prompt: 'Quiero conectar mi ERP con Teselly.'
  },
  {
    key: 'team',
    icon: IconUsers,
    capability: 'clients-management:account:members:create',
    title: 'Tu equipo',
    detail: 'Invitá usuarios y asigná roles',
    prompt: 'Quiero invitar a mi equipo y configurar sus permisos.'
  }
] as const;

/**
 * Connectors this screen never offers, whatever the flags say.
 *
 * `testchannel` is a development fixture. It is flag-gated in the Integrations
 * modal, which is right THERE — a developer with the flag on wants it in the
 * connector list. It is NOT right here: this is the first screen a new account
 * ever sees, and "Canal de prueba" is not one of the real ways to sell. Gating
 * it behind the same flag was not enough: the flag is ON in development, which
 * is exactly where the wizard gets looked at.
 */
const EXCLUDED_FROM_WIZARD = ['testchannel'];

/**
 * The connector that gets the featured 2×2 tile, always.
 *
 * Not "whatever sorts first" — that is an ordering decision impersonating a
 * recommendation, and it changes the moment a connector is renamed.
 */
const FEATURED_CONNECTOR_ID = 'mercadolibre';

const initials = ( name: string ): string =>
  name.replace( /[^A-Za-zÀ-ÿ0-9 ]/g, '' ).split( /\s+/ )
.filter( Boolean )
    .slice( 0, 2 )
.map(( w ) => w[0])
.join( '' )
.toUpperCase() || '?';

export const OnboardingWizard = () => {
  const { t, i18n } = useTranslation( 'interactions' );
  const { backend } = useBackend() as BackendContextType;
  const dispatch = useOnboardingWizardDispatch();
  const { openPanel, setPanelMode, sendMessage } = useAIAssistantActions();
  const navigate = useNavigate();

  const open = useOnboardingWizardSelector( sel.isOpen );
  const step = useOnboardingWizardSelector( sel.step );
  const selectedConnectorId = useOnboardingWizardSelector( sel.selectedConnectorId );
  const connectPanelOpen = useOnboardingWizardSelector( sel.connectPanelOpen );
  const isWaiting = useOnboardingWizardSelector( sel.isWaiting );
  const hasTimedOut = useOnboardingWizardSelector( sel.hasTimedOut );
  const connectedLabel = useOnboardingWizardSelector( sel.connectedLabel );
  const connectedConnectorId = useOnboardingWizardSelector( sel.connectedConnectorId );
  const importBatchProcessId = useOnboardingWizardSelector( sel.importBatchProcessId );
  const importUnavailable = useOnboardingWizardSelector( sel.importUnavailable );
  const orderImportBatchProcessId = useOnboardingWizardSelector( sel.orderImportBatchProcessId );
  const orderImportRequested = useOnboardingWizardSelector( sel.orderImportRequested );
  const orderImportUnavailable = useOnboardingWizardSelector( sel.orderImportUnavailable );
  const error = useOnboardingWizardSelector( sel.error );

  const connectors = useSharedIntegrationsSelector(
    ( s ) => integrationSelectors.selectConnectorsByKind( s, 'salesChannel' )
  );

  // Credentials and store URL live HERE and are never dispatched. See the file
  // docblock — the conversation is a server-persisted transcript.
  const [ storeUrl, setStoreUrl ] = useState( '' );
  const [ apiKeyValues, setApiKeyValues ] = useState<Record<string, string>>({});
  const [ busy, setBusy ] = useState( false );

  // Direct-provision (tienda física): the name, and only the name.
  const [ storeName, setStoreName ] = useState( '' );

  /**
   * Focus the first field the moment the form sheet appears.
   *
   * The fields carry no box — only a rule under them — so an unfocused sheet
   * reads as text rather than as something you type into. That was reported
   * directly. The caret is what makes it obviously a form.
   */
  const sheetRef = useRef<HTMLDivElement | null>( null );

  useEffect(() => {
    if ( !connectPanelOpen ) return;
    const raf = requestAnimationFrame(() => {
      sheetRef.current?.querySelector( 'input' )?.focus();
    });
    return () => cancelAnimationFrame( raf );
  }, [ connectPanelOpen, selectedConnectorId ]);

  const lang = i18n.language?.split( '-' )[0] || 'es';
  const labelOf = ( c: ConnectorEntry ): string =>
    c.label?.[lang] || c.label?.es || c.id;

  /**
   * What happens once you pick this one, derived from the connector's declared
   * capabilities. There is no per-connector description anywhere in the
   * readmodel and no duration either — this line is the honest substitute for
   * both: it sets the expectation with a fact instead of a guess.
   */
  const setupCopy = ( c: ConnectorEntry ): string => {
    switch ( resolveSetupPanelKind( c )) {
      case 'oauth': return t( 'onboarding_wizard_kind_oauth', 'Autorizás con tu cuenta' );
      case 'apiKey': return t( 'onboarding_wizard_kind_apikey', 'Con credenciales de tu tienda' );
      case 'directProvision': return t( 'onboarding_wizard_kind_direct', 'Le ponés un nombre y listo' );
      default: return t( 'onboarding_wizard_kind_other', 'Se configura en Integraciones' );
    }
  };

  const options = useMemo(() => {
    const offered = connectors.filter(( c ) => !EXCLUDED_FROM_WIZARD.includes( c.id ));
    const featured = offered.find(( c ) => c.id === FEATURED_CONNECTOR_ID );
    const rest = offered
      .filter(( c ) => c.id !== FEATURED_CONNECTOR_ID )
      .sort(( a, b ) => labelOf( a ).localeCompare( labelOf( b )));
    // `featured` can legitimately be absent — a connector can be withdrawn
    // backend-side — so the list must not depend on it existing.
    return featured ? [ featured, ...rest ] : rest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ connectors, lang ]);

  const chosen = options.find(( c ) => c.id === selectedConnectorId );
  const panelKind = resolveSetupPanelKind( chosen );
  const needsStoreUrl = panelKind === 'oauth' && Boolean( chosen?.settings?.needsUrl );
  const apiKeyFields = useMemo(
    () => ( chosen ? apiKeyFieldSchemas( t )[chosen.id] ?? [] : []),
    [ chosen, t ]
  );

  /**
   * Whether this connector can be finished inside the wizard.
   *
   * `oauth`, `api-key` AND `direct-provision` all can. Direct provision was
   * originally handed off to Integrations, which meant picking "Tienda física"
   * threw the user OUT of the wizard mid-flow and never brought them back — the
   * one thing this screen exists to avoid. It needs a name and, for
   * `retailstore`, a warehouse / price-list association; that is three fields,
   * not a reason to leave.
   *
   * `serverConfigured` still hands over: it is not a self-service setup at all,
   * so there is no form to show.
   */
  const canFinishHere =
    panelKind === 'oauth' || panelKind === 'apiKey' || panelKind === 'directProvision';
  const needsPanel = needsStoreUrl || panelKind === 'apiKey' || panelKind === 'directProvision';
  const isDirectProvision = panelKind === 'directProvision';


  /**
   * Whether the OPEN panel has what it needs. Only meaningful once the panel is
   * showing — before that the primary button's job is to open it, and disabling
   * it for missing fields the user cannot see yet would be a dead end.
   */
  const panelSatisfied = !connectPanelOpen || (
    ( !needsStoreUrl || storeUrl.trim().length > 0 ) &&
    ( panelKind !== 'apiKey' || apiKeyFields.every(
      ( f ) => ( apiKeyValues[f.name] ?? '' ).trim().length > 0 
)) &&
    ( !isDirectProvision || storeName.trim().length > 0 )
  );

  const close = () =>
    dispatch({ type: 'onboardingWizard/ui/close', payload: {}}, { source: 'user' });

  /**
   * Leaving the wizard always leaves the assistant reachable and NOT fullscreen:
   * the whole point of closing is to get the app back, and a fullscreen panel
   * would just be a second thing covering it.
   */
  const closeAndHandOver = ( prompt?: string ) => {
    close();
    setPanelMode( 'floating' );
    // `openPanel` is IDEMPOTENT (TV1-2377 · S2) — unlike `togglePanel`, which
    // would CLOSE an assistant that was already open (the case whenever the
    // user opened Tessy before finishing the wizard).
    openPanel();
    if ( prompt ) sendMessage( prompt );
  };

  const goToIntegrations = () => {
    if ( !chosen ) return;
    close();
    // Router navigation, not `window.location`: a full reload would drop the
    // conversation this flow just wrote and re-run the whole app bootstrap.
    navigate( `/integrations/channels?connect=${ encodeURIComponent( chosen.id ) }` );
  };

  const startConnect = async () => {
    if ( !chosen ) return;

    if ( !canFinishHere ) {
      goToIntegrations();
      return;
    }

    /**
     * Open the sheet BEFORE doing anything else.
     *
     * When the connector asks for fields, that is the whole point — you cannot
     * fill in what is not on screen, so this returns and waits.
     *
     * When it asks for nothing (Mercado Libre, Amazon), the sheet still opens,
     * and the call continues in the same action so it does not cost an extra
     * click. The reason to open it anyway is the WAIT: the OAuth round-trip
     * happens in another tab, and "terminá la autorización" with no indication
     * of WHICH channel is a message about nothing.
     */
    if ( !connectPanelOpen ) {
      dispatch(
        { type: 'onboardingWizard/channel/openConnectPanel', payload: {}},
        { source: 'user' }
      );
      if ( needsPanel ) return;
    }

    setBusy( true );
    try {
      if ( isDirectProvision ) {
        const integrationUserId = uuidv7();
        await backend.commands.ProvisionSystemIntegration({
          id: `${chosen.id}_${integrationUserId}`,
          body: {
            connectorId: chosen.id,
            integrationUserId,
            channelUserName: storeName.trim(),
            label: storeName.trim(),
            /**
             * Empty on purpose. The wizard asks for the NAME and nothing else:
             * the warehouse / price-list association is not what makes the
             * channel exist, and putting two selects in front of a first-time
             * user to answer a question they have no basis to answer yet is
             * three clicks of ceremony. It is configured afterwards from the
             * channel's own setup — the same place the other direct-provision
             * connectors, which already provision with `{}`, use.
             */
            settings: {}
          }
        });
      } else if ( panelKind === 'oauth' ) {
        const cleaned = storeUrl.trim().replace( /^https?:\/\//, '' );
        const params: { connectorId: string; route: string; site?: string; storeUrl?: string } =
          needsStoreUrl
            ? { connectorId: chosen.id, route: 'get-authorization-url', storeUrl: `https://${cleaned}` }
            : { connectorId: chosen.id, route: 'get-authorization-url', site: 'MLA' };

        // The generated client type for this endpoint is stale — it declares
        // only `{ connectorId, route }` and a `void` return, but the endpoint
        // accepts `site` / `storeUrl` and returns `{ url }`. Same assertion the
        // Integrations modal makes; both call the one endpoint.
        const authResult = ( await backend.get.systems_integrations_webhooks_get(
          params
        )) as unknown as { url?: string };

        if ( authResult?.url ) {
          window.open( authResult.url, '_blank', 'noopener,noreferrer' );
        }
      } else {
        // Server-side authorization validates the credentials against the
        // provider API, stores them and provisions the integration. The FE never
        // provisions directly.
        await backend.post.systems_integrations_webhooks_post(
          { connectorId: chosen.id, route: 'authorize' },
          apiKeyValues
        );
      }

      // Either way we now wait for the channel to appear in the readmodel; the
      // policy owns that wait and its bound.
      dispatch(
        { type: 'onboardingWizard/connect/started', payload: {}},
        { source: 'user' }
      );
      setApiKeyValues({});
    } catch ( err ) {
      dispatch(
        { type: 'onboardingWizard/connect/failed', payload: {
          message: err instanceof Error
            ? err.message
            : t( 'onboarding_wizard_connect_failed', 'No pudimos conectar el canal.' )
        }},
        { source: 'api' }
      );
    } finally {
      setBusy( false );
    }
  };

  if ( !open ) return null;

  const featured = options[0];
  /** Which of the three views is on screen. The form sheet IS a view, not an
   *  annex of the picker — that is the whole point of the redesign. */
  const view: 'pick' | 'form' | 'next' =
    step === 'nextSteps' ? 'next' : ( connectPanelOpen ? 'form' : 'pick' );

  /**
   * Whether to offer the historical order seeding at all.
   *
   * `canImportOrderHistory` — not a connector id spelled out here. The backend
   * gate is an OPTIONAL method on the channel client (`getTotalOrdersToImport`),
   * so every other connector answers the start command with a 400; offering the
   * control there would be an invitation to an error. One list, in
   * `capabilityGates.ts`, shared with the Integrations modal.
   */
  const canOfferOrderImport = canImportOrderHistory( connectedConnectorId ?? undefined );

  const sheetFields = [
    ...( needsStoreUrl
      ? [{
        name: 'storeUrl',
        label: t( 'onboarding_wizard_store_url', 'Dirección de tu tienda' ),
        help: t( 'onboarding_wizard_store_url_help', 'La usamos para llevarte a autorizar en tu propia tienda.' ),
        placeholder: 'mitienda.com',
        secret: false,
        value: storeUrl,
        onChange: setStoreUrl
      }]
      : []),
    ...( isDirectProvision
      ? [{
        name: 'storeName',
        label: t( 'onboarding_wizard_store_name', 'Nombre de la tienda' ),
        help: t( 'onboarding_wizard_store_name_help', 'Con esto la vas a identificar entre tus canales.' ),
        placeholder: t( 'onboarding_wizard_store_name_placeholder', 'Sucursal Centro' ),
        secret: false,
        value: storeName,
        onChange: setStoreName
      }]
      : []),
    ...apiKeyFields.map(( f ) => ({
      name: f.name,
      label: f.label,
      help: '',
      placeholder: f.placeholder ?? '',
      secret: Boolean( f.secret ),
      value: apiKeyValues[f.name] ?? '',
      onChange: ( v: string ) => setApiKeyValues(( prev ) => ({ ...prev, [f.name]: v }))
    }))
  ];

  return (
    <div className={classes.screen} data-testid={ids.root} role="dialog" aria-modal="true">
      <div className={classes.wrap}>
        <header className={classes.head}>
          <span className={classes.brand}>
            <Image src={logo} height={24} width="auto" alt="" />
            Teselly
          </span>
          <span className={classes.crumb}>
            <span className={classes.crumbN}>{view === 'next' ? 2 : 1}</span>
            {view === 'next'
              ? t( 'onboarding_wizard_step_2', 'Dejá el resto listo' )
              : t( 'onboarding_wizard_step_1', 'Elegí tu canal' )}
          </span>
          <button
            type="button"
            className={classes.exit}
            onClick={() => closeAndHandOver()}
            data-testid={ids.close}
          >
            {t( 'onboarding_wizard_close', 'Cerrar y seguir con Tessy' )}
          </button>
        </header>

        {/* ── 1 · elección ────────────────────────────────────────────── */}
        {view === 'pick' && (
          <section className={classes.section}>
            <div className={classes.intro}>
              <h1 className={classes.h1}>
                {t( 'onboarding_wizard_title_1', 'Bienvenido. Empecemos por donde vendés.' )}
              </h1>
              <p className={classes.lede}>
                {t(
                  'onboarding_wizard_lede_1',
                  'Conectá tu primer canal y traemos publicaciones, stock, precios y órdenes automáticamente. Después sumás los que quieras.'
                )}
              </p>
            </div>

            {options.length === 0 ? (
              <div className={classes.emptyState}>
                <div className={classes.emptyStateIcon}><IconPlugConnected size={20} /></div>
                <div className={classes.emptyStateTitle}>
                  {t( 'onboarding_wizard_empty_title', 'No hay canales disponibles' )}
                </div>
                <div className={classes.emptyStateDescription}>
                  {t( 'onboarding_wizard_empty_hint', 'Podés seguir y conectarlos más adelante desde Integraciones' )}
                </div>
              </div>
            ) : (
              <div className={classes.bento}>
                {options.map(( connector ) => {
                  const isFeat = connector.id === featured?.id;
                  const isOn = selectedConnectorId === connector.id;
                  const name = labelOf( connector );
                  return (
                    <button
                      key={connector.id}
                      type="button"
                      className={`${classes.cell} ${isFeat ? classes.feat : ''}`}
                      aria-pressed={isOn}
                      data-testid={ids.channelCell( connector.id )}
                      onClick={() => dispatch(
                        { type: 'onboardingWizard/channel/select', payload: { connectorId: connector.id }},
                        { source: 'user' }
                      )}
                    >
                      <span className={classes.tick}><IconCheck size={11} stroke={3} /></span>
                      <span className={classes.mark}>
                        {connector.imageUrl
                          ? <img src={connector.imageUrl} alt="" />
                          : initials( name )}
                      </span>
                      <span className={classes.cellName}>{name}</span>
                      <span className={classes.cellMeta}>{setupCopy( connector )}</span>
                      {isFeat && (
                        <span className={classes.popular}>
                          {t( 'onboarding_wizard_most_used', 'La opción más elegida' )}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className={classes.bar}>
              <button
                type="button"
                className={classes.btn}
                disabled={!chosen || busy}
                data-testid={ids.connect}
                onClick={() => void startConnect()}
              >
                {chosen
                  ? t( 'onboarding_wizard_connect_named', 'Conectar {{channel}}', { channel: labelOf( chosen ) })
                  : t( 'onboarding_wizard_connect', 'Conectar canal' )}
              </button>
              <button
                type="button"
                className={classes.btnGhost}
                data-testid={ids.skip}
                onClick={() => closeAndHandOver()}
              >
                {t( 'onboarding_wizard_skip', 'Lo hago después' )}
              </button>
              <span className={classes.hint}>
                {t( 'onboarding_wizard_hint_1', 'Podés cambiarlo o sumar más canales cuando quieras' )}
              </span>
            </div>
          </section>
        )}

        {/* ── 1b · la hoja del canal, a pantalla ──────────────────────── */}
        {view === 'form' && chosen && (
          <section className={classes.section}>
            <button
              type="button"
              className={classes.back}
              onClick={() => dispatch(
                { type: 'onboardingWizard/channel/closeConnectPanel', payload: {}},
                { source: 'user' }
              )}
            >
              ← {t( 'onboarding_wizard_back', 'Volver a los canales' )}
            </button>

            <div className={classes.sheet} ref={sheetRef}>
              <div className={classes.sheetHead}>
                <span className={classes.mark}>
                  {chosen.imageUrl
                    ? <img src={chosen.imageUrl} alt="" />
                    : initials( labelOf( chosen ))}
                </span>
                <div>
                  <h2 className={classes.sheetTitle}>
                    {t( 'onboarding_wizard_connect_named', 'Conectar {{channel}}', { channel: labelOf( chosen ) })}
                  </h2>
                  <div className={classes.sheetSub}>{setupCopy( chosen )}</div>
                </div>
              </div>

              {sheetFields.length > 0 && (
                <div className={classes.form}>
                  {sheetFields.map(( f ) => (
                    <div className={classes.fld} key={f.name}>
                      <label className={classes.fldLabel} htmlFor={`ow-${f.name}`}>{f.label}</label>
                      {f.help && <div className={classes.fldHelp}>{f.help}</div>}
                      <input
                        id={`ow-${f.name}`}
                        className={classes.fldInput}
                        type={f.secret ? 'password' : 'text'}
                        placeholder={f.placeholder}
                        value={f.value}
                        onChange={( e ) => f.onChange( e.currentTarget.value )}
                        data-testid={
                          f.name === 'storeUrl' ? ids.storeUrl
                            : f.name === 'storeName' ? ids.storeName
                              : ids.apiKeyField( f.name )
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              {panelKind === 'apiKey' && (
                <span className={classes.notice}>
                  {t(
                    'onboarding_wizard_credentials_notice',
                    'Tus credenciales viajan directo al canal. No quedan en la conversación.'
                  )}
                </span>
              )}
            </div>

            {error && <div className={classes.error}>{error}</div>}

            {isWaiting && (
              <div className={classes.waiting}>
                <Loader size={14} />
                {t(
                  'onboarding_wizard_waiting',
                  'Terminá la autorización en la otra pestaña. Apenas el canal aparezca, seguimos.'
                )}
                <button
                  type="button"
                  className={classes.linkBtn}
                  data-testid={ids.cancelWait}
                  onClick={() => dispatch(
                    { type: 'onboardingWizard/connect/cancelWait', payload: {}},
                    { source: 'user' }
                  )}
                >
                  {t( 'onboarding_wizard_cancel_wait', 'Cancelar y elegir otro' )}
                </button>
              </div>
            )}

            {hasTimedOut && (
              <div className={classes.waiting}>
                {t(
                  'onboarding_wizard_timed_out',
                  'Todavía no vemos el canal. Si ya autorizaste, dale a revisar de nuevo.'
                )}
                <button
                  type="button"
                  className={classes.linkBtn}
                  data-testid={ids.recheck}
                  onClick={() => dispatch(
                    { type: 'onboardingWizard/connect/retryWait', payload: {}},
                    { source: 'user' }
                  )}
                >
                  {t( 'onboarding_wizard_recheck', 'Revisar de nuevo' )}
                </button>
              </div>
            )}

            <div className={classes.bar}>
              <button
                type="button"
                className={classes.btn}
                disabled={busy || isWaiting || !panelSatisfied}
                data-testid={ids.connect}
                onClick={() => void startConnect()}
              >
                {busy && <Loader size={13} color="white" />}
                {t( 'onboarding_wizard_connect_named', 'Conectar {{channel}}', { channel: labelOf( chosen ) })}
              </button>
              <button
                type="button"
                className={classes.btnGhost}
                onClick={() => dispatch(
                  { type: 'onboardingWizard/channel/closeConnectPanel', payload: {}},
                  { source: 'user' }
                )}
              >
                {t( 'onboarding_wizard_cancel', 'Cancelar' )}
              </button>
            </div>
          </section>
        )}

        {/* ── 2 · progreso arriba, opciones abajo ─────────────────────── */}
        {view === 'next' && (
          <section className={classes.section}>
            <div className={classes.intro}>
              <h1 className={classes.h1}>
                {connectedLabel
                  ? t( 'onboarding_wizard_title_2_named', '{{channel}} quedó conectado.', { channel: connectedLabel })
                  : t( 'onboarding_wizard_title_2', 'Mientras importamos, dejemos el resto listo.' )}
              </h1>
              <p className={classes.lede}>
                {t(
                  'onboarding_wizard_lede_2',
                  'La importación sigue en segundo plano, así que podés cerrar esto cuando quieras. Elegí qué configurar y el asistente te guía.'
                )}
              </p>
            </div>

            {/* The SAME block the conversation renders — one component, so the
                wizard and Tessy cannot disagree about the progress. Two runs, two
                blocks: the publications import and the historical order seeding
                are separate batch processes and either can fail alone.

                Every branch below renders SOMETHING. The reported bug was this
                area rendering nothing at all while the screen claimed a catalog
                was "being brought in in the background" — so a state with no run
                attached says exactly that, rather than showing a bar with no
                run behind it. */}
            <div className={classes.progress}>
              {importBatchProcessId ? (
                <ImportProgressBlock
                  block={{
                    type: 'import-progress',
                    payload: {
                      batchProcessId: importBatchProcessId,
                      channelLabel: connectedLabel ?? undefined,
                      kind: 'publications'
                    }
                  }}
                  messageId="onboarding-wizard"
                />
              ) : (
                <p
                  className={classes.progressNote}
                  data-testid={ids.progressNote( 'publications' )}
                >
                  {importUnavailable
                    ? t(
                      'onboarding_wizard_import_unavailable',
                      'El canal quedó conectado, pero no pudimos arrancar la importación de publicaciones. Podés lanzarla desde Integraciones.'
                    )
                    : t(
                      'onboarding_wizard_import_preparing',
                      'Preparando la importación de tus publicaciones…'
                    )}
                </p>
              )}

              {orderImportBatchProcessId ? (
                <ImportProgressBlock
                  block={{
                    type: 'import-progress',
                    payload: {
                      batchProcessId: orderImportBatchProcessId,
                      channelLabel: connectedLabel ?? undefined,
                      kind: 'orders'
                    }
                  }}
                  messageId="onboarding-wizard-orders"
                />
              ) : orderImportUnavailable || orderImportRequested ? (
                <p
                  className={classes.progressNote}
                  data-testid={ids.progressNote( 'orders' )}
                >
                  {orderImportUnavailable
                    ? t(
                      'onboarding_wizard_order_import_unavailable',
                      'No pudimos arrancar la importación del historial de pedidos. Podés intentarlo desde Integraciones.'
                    )
                    : t(
                      'onboarding_wizard_order_import_preparing',
                      'Preparando la importación de tu historial de pedidos…'
                    )}
                </p>
              ) : canOfferOrderImport ? (
                /* Opt-IN, not opt-out. Seeding pulls the seller's entire past
                   order history — heavy and hard to undo — which is why the
                   Integrations connect modal defaults its own checkbox to OFF
                   while defaulting publications ON. The wizard keeps that
                   asymmetry rather than deciding for a first-time user. */
                <button
                  type="button"
                  className={classes.orderImport}
                  data-testid={ids.importOrders}
                  onClick={() => dispatch(
                    { type: 'onboardingWizard/orderImport/request', payload: {}},
                    { source: 'user' }
                  )}
                >
                  <IconShoppingCartDown size={15} />
                  <span className={classes.orderImportLabel}>
                    {t( 'onboarding_wizard_order_import_cta', 'Traer también mi historial de pedidos' )}
                  </span>
                  <span className={classes.orderImportHint}>
                    {t(
                      'onboarding_wizard_order_import_hint',
                      'Traemos todos tus pedidos anteriores del canal. Puede tardar un rato.'
                    )}
                  </span>
                </button>
              ) : null}
            </div>

            <div className={classes.nextHead}>
              {t( 'onboarding_wizard_next_head', '¿Qué querés dejar listo?' )}
            </div>

            <div className={classes.bento}>
              {NEXT_OPTIONS.map(( option ) => (
                <OptionCell
                  key={option.key}
                  option={option}
                  onPick={() => closeAndHandOver( option.prompt )}
                />
              ))}
            </div>

            <div className={classes.bar}>
              <button
                type="button"
                className={classes.btnGhost}
                data-testid={ids.finish}
                onClick={() => closeAndHandOver()}
              >
                {t( 'onboarding_wizard_go_dashboard', 'Ir al panel' )}
                <IconArrowRight size={14} />
              </button>
              <span className={classes.hint}>
                {t( 'onboarding_wizard_hint_2', 'Te avisamos cuando la importación termine' )}
              </span>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

type OptionCellProps = {
  option: typeof NEXT_OPTIONS[number];
  onPick: () => void;
};

/**
 * Split out so the capability hook runs once per option — `useCan` is a hook and
 * cannot be called inside the `.map` callback of the parent.
 */
const OptionCell = ({ option, onPick }: OptionCellProps ) => {
  const { t } = useTranslation( 'interactions' );
  const allowed = useCan( option.capability );
  if ( !allowed ) return null;

  const Icon = option.icon;
  return (
    <button
      type="button"
      className={classes.cell}
      data-testid={onboardingWizardTestIdsOption( option.key )}
      onClick={onPick}
    >
      <span className={classes.optionIcon}><Icon size={19} /></span>
      <span className={classes.cellName}>
        {t( `onboarding_wizard_option_${option.key}_title`, option.title )}
      </span>
      <span className={classes.cellMeta}>
        {t( `onboarding_wizard_option_${option.key}_detail`, option.detail )}
      </span>
    </button>
  );
};

const onboardingWizardTestIdsOption = ( key: string ) => ids.option( key );

export default OnboardingWizard;
